const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

export function stringifyTableValue(value) {
  if (value == null) return '';
  if (Array.isArray(value)) {
    return value.map(stringifyTableValue).join(' ');
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  return String(value).trim();
}

export function compareTableValues(left, right, direction = 'asc') {
  const leftValue = stringifyTableValue(left);
  const rightValue = stringifyTableValue(right);

  if (!leftValue && !rightValue) return 0;
  if (!leftValue) return 1;
  if (!rightValue) return -1;

  const leftNumber = Number(leftValue);
  const rightNumber = Number(rightValue);
  const bothNumeric = !Number.isNaN(leftNumber) && !Number.isNaN(rightNumber);

  const comparison = bothNumeric
    ? leftNumber - rightNumber
    : collator.compare(leftValue, rightValue);

  return direction === 'desc' ? comparison * -1 : comparison;
}

export function matchesColumnFilter(value, filterValue) {
  if (!filterValue) return true;

  return stringifyTableValue(value)
    .toLowerCase()
    .includes(filterValue.trim().toLowerCase());
}

export function getNextSortConfig(currentConfig, key) {
  if (!currentConfig || currentConfig.key !== key) {
    return { key, direction: 'asc' };
  }

  if (currentConfig.direction === 'asc') {
    return { key, direction: 'desc' };
  }

  return null;
}

export function getSortIndicator(sortConfig, key) {
  if (!sortConfig || sortConfig.key !== key) return '';
  return sortConfig.direction === 'asc' ? ' ^' : ' v';
}

function toComparableDate(value) {
  if (!value) return null;

  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed;
  }

  if (typeof value === 'string' && /^\d{2}:\d{2}/.test(value)) {
    return null;
  }

  return null;
}

function toMinutes(value) {
  if (!value || typeof value !== 'string') return null;

  const match = value.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return (hours * 60) + minutes;
}

export function getUniqueOptions(items, getValue) {
  return Array.from(new Set(
    items
      .map(getValue)
      .flatMap(value => Array.isArray(value) ? value : [value])
      .map(value => value == null ? '' : String(value).trim())
      .filter(Boolean)
  )).sort(collator.compare);
}

export function toLocalDateInputValue(value) {
  const parsed = toComparableDate(value);
  if (!parsed) {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    return '';
  }

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function toLocalDateTimeInputValue(value) {
  const parsed = toComparableDate(value);
  if (!parsed) return '';

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  const hours = String(parsed.getHours()).padStart(2, '0');
  const minutes = String(parsed.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

export function matchesSelectFilter(value, selectedValue) {
  if (!selectedValue) return true;
  return String(value ?? '').trim().toLowerCase() === selectedValue.trim().toLowerCase();
}

export function matchesTriStateFilter(value, selectedValue) {
  if (!selectedValue || selectedValue === 'any') return true;

  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === selectedValue.toLowerCase();
}

export function matchesDateFilter(value, selectedDate) {
  if (!selectedDate) return true;
  return toLocalDateInputValue(value) === selectedDate;
}

export function matchesDateRangeFilter(value, range = {}) {
  if (!range.from && !range.to) return true;

  const parsed = toComparableDate(value);
  if (!parsed) return false;

  const localDate = toLocalDateInputValue(parsed);

  if (range.from && localDate < range.from) return false;
  if (range.to && localDate > range.to) return false;
  return true;
}

export function matchesDateTimeRangeFilter(value, range = {}) {
  if (!range.from && !range.to) return true;

  const parsed = toComparableDate(value);
  if (!parsed) return false;

  const comparableValue = toLocalDateTimeInputValue(parsed);

  if (range.from && comparableValue < range.from) return false;
  if (range.to && comparableValue > range.to) return false;
  return true;
}

export function matchesTimeRangeFilter(value, range = {}) {
  if (!range.from && !range.to) return true;

  const minutes = toMinutes(value);
  if (minutes == null) return false;

  const fromMinutes = toMinutes(range.from);
  const toMinutesValue = toMinutes(range.to);

  if (fromMinutes != null && minutes < fromMinutes) return false;
  if (toMinutesValue != null && minutes > toMinutesValue) return false;
  return true;
}

export function matchesMultiSelectFilter(values, selectedValues = []) {
  if (!selectedValues.length) return true;

  const normalizedValues = (Array.isArray(values) ? values : [values])
    .map(value => String(value ?? '').trim())
    .filter(Boolean);

  return selectedValues.some(selected => normalizedValues.includes(String(selected)));
}
